import { useState } from "react";
import { Info, ListChecks } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils/cn";

import { useSaveGoals } from "../../../api/hooks";
import type { CatalogueGoal, CatalogueThematic, GoalsState } from "../../../types";
import { CheckRow } from "./controls";

/**
 * Part 2 (after CQDD approval) — per declared enjeu, choose objectives; per
 * objective, choose the eligible action paths. Selecting an objective ticks
 * none of its actions — the expert ticks each one (as on the Django step); the
 * action list opens in a modal so the objective list stays light. Objectives with no selectable
 * action are filtered out server-side, so every listed objective has actions.
 * Saved via PUT — the first completion publishes immediately server-side.
 */
export function GoalsStep({
  catalogue,
  selectedIssueIds,
  goalsState,
  onSaved,
  onBack,
}: {
  catalogue: CatalogueThematic[];
  selectedIssueIds: Set<string>;
  goalsState: GoalsState;
  onSaved: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const save = useSaveGoals();
  const [goals, setGoals] = useState<Set<string>>(new Set(goalsState.goals));
  const [actions, setActions] = useState<Set<string>>(new Set(goalsState.actions));
  const [openGoalId, setOpenGoalId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const issues = catalogue
    .flatMap((thematic) => thematic.issues)
    .filter((issue) => selectedIssueIds.has(issue.id));
  const openGoal = issues
    .flatMap((issue) => issue.goals)
    .find((goal) => goal.id === openGoalId);

  const toggleGoal = (goalId: string, actionIds: string[], on: boolean) => {
    setGoals((prev) => {
      const next = new Set(prev);
      if (on) next.add(goalId);
      else next.delete(goalId);
      return next;
    });
    // Selecting an objective ticks none of its actions — the expert ticks each
    // one, which has them read it (the Django step does the same);
    // deselecting clears them.
    if (!on)
      setActions((prev) => {
        const next = new Set(prev);
        for (const id of actionIds) next.delete(id);
        return next;
      });
  };

  const toggleAction = (actionId: string, on: boolean) =>
    setActions((prev) => {
      const next = new Set(prev);
      if (on) next.add(actionId);
      else next.delete(actionId);
      return next;
    });

  const selectedActionCount = (goal: CatalogueGoal) =>
    goal.actions.filter((a) => actions.has(a.id)).length;

  // An objective switched on with no action ticked cannot be saved — the rule
  // registration_sync.validate_actions applies to both apps. With nothing
  // pre-ticked it is the likely slip, so it is caught before the round trip.
  const [emptyGoalIds, setEmptyGoalIds] = useState<Set<string>>(new Set());

  const handleSave = () => {
    setFormError(null);
    const empty = new Set(
      issues
        .flatMap((issue) => issue.goals)
        .filter((goal) => goals.has(goal.id) && selectedActionCount(goal) === 0)
        .map((goal) => goal.id),
    );
    setEmptyGoalIds(empty);
    if (empty.size > 0) {
      setFormError(t("expert.register.validation.fix_indicated"));
      return;
    }
    // Save the objectives/actions selection. Server-side, the first completion
    // by an approved expert publishes immediately; a later edit stages for
    // re-validation. Either way, return to the dashboard on success.
    save.mutate(
      { goals: [...goals], actions: [...actions] },
      {
        onSuccess: (result) => {
          // The Django step flashes the same sentence when objectives go live.
          if (result?.published) toast.success(t("expert.register.profileCompleted"));
          onSaved();
        },
        onError: (err: unknown) => {
          const data = err as { detail?: string };
          setFormError(data.detail ?? t("auth.genericError"));
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <p className="pt-2 text-sm text-muted-foreground">{t("expert.register.goalsHelp")}</p>
      {(goalsState.pending_issue_ids?.length ?? 0) > 0 && (
        <p
          className="flex items-start gap-1.5 rounded-lg border border-info/40 bg-info/10 p-3 text-sm"
          data-testid="pending-issues-notice"
        >
          <Info className="mt-0.5 size-4 flex-none" aria-hidden />
          {t("expert.register.pendingIssuesNotice")}
        </p>
      )}

      {issues.map((issue) => (
        <section key={issue.id} className="space-y-2 rounded-lg border p-3">
          <h3 className="font-medium">{issue.name}</h3>
          {issue.goals.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t("expert.register.noObjectives")}
            </p>
          )}
          <div className="space-y-2">
            {issue.goals.map((goal) => {
              const actionIds = goal.actions.map((a) => a.id);
              const goalOn = goals.has(goal.id);
              const count = selectedActionCount(goal);
              return (
                <div
                  key={goal.id}
                  className={cn(
                    "flex items-center justify-between gap-3 rounded-md border px-3 py-2",
                    goalOn && emptyGoalIds.has(goal.id) ? "border-destructive" : "border-border",
                  )}
                >
                  <span className="text-sm">
                    {goal.name}
                    {goalOn && emptyGoalIds.has(goal.id) && count === 0 && (
                      <span className="block text-xs text-destructive">
                        {t("expert.register.validation.goal_needs_action")}
                      </span>
                    )}
                  </span>
                  <div className="flex flex-shrink-0 items-center gap-3">
                    {goalOn && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setOpenGoalId(goal.id)}
                      >
                        <ListChecks className="mr-1 size-4" aria-hidden />
                        {t("expert.register.actionsLabel")} ({count})
                      </Button>
                    )}
                    <Switch
                      checked={goalOn}
                      onCheckedChange={(v) => {
                        toggleGoal(goal.id, actionIds, v);
                        // Selecting an objective opens its actions for the
                        // expert to tick the paths they offer (as the old app).
                        if (v) setOpenGoalId(goal.id);
                      }}
                      label={goal.name}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {formError && <p className="text-sm font-medium text-destructive">{formError}</p>}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          {t("expert.objectives.backToDashboard")}
        </Button>
        <Button onClick={handleSave} disabled={save.isPending}>
          {t("expert.register.save")}
        </Button>
      </div>

      {openGoal && (
        <Modal
          title={`${t("expert.register.actionsModalTitle")} — ${openGoal.name}`}
          onClose={() => setOpenGoalId(null)}
        >
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t("expert.register.actionsModalIntro")}
            </p>
            {openGoal.actions.map((action) => (
              <CheckRow
                key={action.id}
                label={action.name}
                checked={actions.has(action.id)}
                onChange={(v) => toggleAction(action.id, v)}
              />
            ))}
            <div className="flex justify-end border-t pt-4">
              <Button type="button" onClick={() => setOpenGoalId(null)}>
                {t("expert.register.done")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
